import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function StockportCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Stockport Pothole Claims | 798km of U-Roads, 11% RED | Fixtyer"
        description="Stockport Council earns GREEN on spend but AMBER overall — 797.7km of unclassified roads with 11% in RED condition, its £100.6m Highway Investment Programme finished, and 16,482 carriageway defects repaired in five years. See what the DfT rating means for your claim."
        canonical="/councils/stockport"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Stockport', url: '/councils/stockport' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Stockport has a GREEN spend scorecard — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but Stockport\'s overall rating is AMBER because of road condition. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably inspected and repaired — not on aggregate spend. In 2024/25 capital spend was £4.1m against a DfT allocation of £5.27m, and preventative maintenance is projected at just 23% for 2025/26.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 797.7km — 82% of Stockport\'s 969.8km carriageway network. At the latest survey, 11% of U-roads were in RED condition — roughly 88km of residential streets and estate roads. The council assesses carriageway and footway condition over a two-year period, so any given road may go up to two years between condition surveys.',
          },
          {
            question: 'Does the finished Highway Investment Programme affect my claim?',
            answer: 'It can strengthen it. Stockport borrowed £100.6m for its Highway Investment Programme to cut RED roads to 10%, but the council states this extra spending ceased as of 2023/24 and "the condition of the network going forwards will be more dependent on the level of external funding." That is an admission that past improvements may not be sustained.',
          },
          {
            question: 'Why does Stockport only publish RED percentages for U-roads?',
            answer: 'The DfT template requires only RED-category percentages for unclassified roads. Stockport does not publish amber or green breakdowns for U-roads in its transparency report — so you cannot see what proportion is borderline versus genuinely good. For claims, the 11% RED figure and the two-year survey cycle are the structural data points.',
          },
          {
            question: 'The pothole figures include drainage repairs — does that weaken my claim?',
            answer: 'No — read the footnote, do not dismiss the scale. Stockport\'s figures count carriageway defects attended to under its highway safety inspection and repair policy, including drainage investigations. The council filled 3,716 such defects in 2024/25 alone and anticipates approximately 4,200 in 2025/26. That volume shows how many road-surface failures the network produces each year.',
          },
          {
            question: 'Stockport surveys condition over two years — what does that mean for Section 58?',
            answer: 'The council states that "General highway condition of carriageways and footways within in the borough are assessed over a 2 year period." For Section 58, ask when your road was last GAIST-surveyed, whether routine safety inspections under the council\'s Inspection and Repair Policy would have caught the defect between surveys, and whether prior reports gave the council actual notice regardless of survey timing.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Stockport Council"
        councilSlug="stockport"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/stockport"
        headline="Stockport Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Stockport</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Stockport: GREEN Spend, 88km of Residential Roads in RED
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Stockport earns a <strong className="text-emerald-400">GREEN</strong> spend scorecard and repaired
            <strong className="text-rose-400"> 16,482 carriageway defects in five years</strong>. Yet the overall
            DfT rating is <strong className="text-amber-400">AMBER</strong> — because 797.7km of unclassified roads
            (82% of the network) include <strong className="text-rose-400">11% in RED condition</strong>, its
            £100.6m Highway Investment Programme has finished, and preventative capital spend is projected at just 23% for 2025/26.
          </p>

          {/* Key stat callout */}
          <div className="bg-amber-500/20 border border-amber-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-amber-400">797.7km</div>
            <div className="text-white font-medium mt-2">Unclassified roads — 82% of the network</div>
            <div className="text-brand-300 text-sm mt-1">
              11% in RED condition at the latest survey — roughly 88km of residential streets, estate roads and
              village routes across Stockport's 969.8km carriageway network.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of GAIST survey data from Stockport's own transparency report — A-roads improving,
            B and C roads slipping, U-road RED stuck at 10–11%
          </p>

          {/* A-roads */}
          <div className="bg-surface-50 border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">A-roads (95.7km — 9.9% of network): improving</h3>
            <div className="grid grid-cols-3 gap-4 mb-4">
              <div className="text-center">
                <div className="text-3xl font-bold text-rose-600">4%</div>
                <div className="text-sm text-rose-800">RED (2024)</div>
                <div className="text-xs text-surface-500">down from 6% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-amber-600">40%</div>
                <div className="text-sm text-amber-800">Amber</div>
                <div className="text-xs text-surface-500">up from 39% in 2020</div>
              </div>
              <div className="text-center">
                <div className="text-3xl font-bold text-emerald-600">56%</div>
                <div className="text-sm text-emerald-800">Green</div>
                <div className="text-xs text-surface-500">up from 55% in 2020</div>
              </div>
            </div>
            <p className="text-surface-600 text-sm">
              Main roads have genuinely improved — RED A-roads halved from 6% to 4% over five years. But A-roads
              are barely one-tenth of Stockport's network.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (76.4km — 7.9% of network): declining</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">7%</td>
                    <td className="px-4 py-2 text-center">53%</td>
                    <td className="px-4 py-2 text-center">40%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">51%</td>
                    <td className="px-4 py-2 text-center">43%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">51%</td>
                    <td className="px-4 py-2 text-center">43%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">6%</td>
                    <td className="px-4 py-2 text-center">51%</td>
                    <td className="px-4 py-2 text-center">43%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">6%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">54%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">40%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              GREEN-condition B and C roads fell from 43% to 40% in 2024, while amber rose to 54%. Sixty percent
              of Stockport's classified local roads now need — or will soon need — maintenance.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend — But The Programme Is Over</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£6.03m</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-amber-400">23%</div>
                <div className="text-brand-300 text-sm">Projected preventative share 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-rose-400">£4.1m</div>
                <div className="text-brand-300 text-sm">Actual capital spend 2024/25 (vs £5.27m allocated)</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Stockport's £100.6m Highway Investment Programme cut RED roads from 18.6% to around 10% — but the
              council states that programme <span className="text-rose-400">has now finished</span>, and future
              condition depends on DfT and TfGM funding. Preventative capital is projected at just 23% for 2025/26,
              down from 45% in 2024/25.
            </p>
          </div>
        </div>
      </section>

      {/* The U-road Blind Spot */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The 797.7km Blind Spot
          </h2>
          <p className="text-surface-600 mb-8">
            82% of Stockport's carriageway network is unclassified roads — surveyed on a two-year cycle,
            with only RED percentages published
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">U-roads in RED condition</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">13%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">10%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                </tr>
                <tr>
                  <td className="px-6 py-4 font-mono font-bold text-brand-900">2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">11%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="Our unclassified roads represent the largest proportion of the network and yet the lowest levels of traffic movement."
              source="Stockport Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="General highway condition of carriageways and footways within in the borough are assessed over a 2 year period."
              source="Stockport Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">GAIST Camera Surveys</h3>
              <p className="text-surface-600 text-sm mb-3">
                Stockport uses GAIST to monitor network condition with high-definition cameras that capture images
                along the road creating a "video-like quality" to show asset condition. This imagery helps identify
                changes, monitor degradation, and provide real-time intelligence for road maintenance.
              </p>
              <p className="text-surface-600 text-sm">
                From 2026/27 a new BSI PAS2161 methodology will apply nationally. Stockport states GAIST's system
                is ready to meet those requirements — but that is a future change, not today's survey record.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The RED-Only Reporting Gap</h3>
              <p className="text-amber-800 text-sm mb-3">
                Stockport publishes full RED/amber/green breakdowns for A and B/C roads, but only RED percentages
                for U-roads — as the DfT template requires. You cannot see how many residential streets sit in
                amber (maintenance may be required soon) versus green.
              </p>
              <p className="text-amber-800 text-sm">
                At 11% RED, roughly <strong>88km of U-roads</strong> should be considered for maintenance.
                The true number needing attention soon is higher — but the council does not publish that figure.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, a council must show it had a reasonable system for knowing
              the condition of its roads. For Stockport's unclassified network, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last GAIST-surveyed — and was it in year one or year two of the cycle?</li>
              <li>• If 11% of U-roads are RED, what was done about the defect on your street specifically?</li>
              <li>• Did routine safety inspections under the Inspection and Repair Policy catch it between surveys?</li>
              <li>• Were there prior reports (FixMyStreet, council reports) that gave actual notice regardless of survey timing?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot claim detailed knowledge of 798km of residential roads it measures on a two-year
              rotation — especially when it publishes only the RED slice of the picture.
            </p>
          </div>
        </div>
      </section>

      {/* The Repair Conveyor Belt */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            16,482 Carriageway Defects in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            The scale of reactive repair tells you how many road-surface failures this network produces
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Carriageway defects attended to</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020/21</td>
                  <td className="px-6 py-4 text-center">2,809</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021/22</td>
                  <td className="px-6 py-4 text-center">3,512</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022/23</td>
                  <td className="px-6 py-4 text-center">2,821</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023/24</td>
                  <td className="px-6 py-4 text-center">3,624</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">3,716</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">16,482</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">~9 Defects a Day, Every Day</h3>
              <p className="text-surface-600 text-sm">
                Averaged over five years, Stockport attends to around nine carriageway defects per day. The council
                anticipates filling approximately 4,200 potholes in 2025/26 — roughly 11 every day. A network
                producing defects at that rate is one where potholes routinely form between inspections.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">The Counting Caveat</h3>
              <p className="text-amber-800 text-sm">
                Stockport's footnote: "These figures are based on the number of carriageway defects attended to.
                Due to the way that this data is collated it is not possible to further breakdown the nature of
                the defect." Drainage-related repairs and trial excavations are included. The figures relate to
                defects meeting the council's highway safety inspection and repair policy definition.
              </p>
            </div>
          </div>

          <QuoteCard
            quote="We anticipate filling in approximately 4200 potholes as part of our reactive repairs in 2025/26. This is based on figures from previous years."
            source="Stockport Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* The HIP Admission */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Finished Highway Investment Programme
          </h2>
          <p className="text-surface-600 mb-8">
            Stockport's own explanation for where road condition goes next — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="In 2014/15 Stockport begin a Highway Investment Programme (HIP) which borrowed funds to undertake Highway repairs of £100.6m on both footways and carriageways including some drainage work to support the longevity of the highway improvements provided. The project aim was to reduce the overall percentage of red roads to 10% for both carriageways and footways by the end of the programme. The improvement of this work is still visible in the overall network condition for the borough. However, this extra spending has ceased as of 2023/24 and therefore the condition of the network going forwards will be more dependent on the level of external funding from the National and Regional Government (DfT and TfGM)."
              source="Stockport Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="The focus on preventative maintenance has been a key part of the delivery strategy however more recently there has been an increased focus on main road treatments and skidding risk as our funding has reduced."
              source="Stockport Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="However, it should be noted that this funding programme has now finished."
              source="Stockport Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What The HIP Achieved</h3>
              <p className="text-surface-600 text-sm mb-3">
                Stockport reports carriageway condition improved from 18.6% RED in 2014 to around 10% overall
                by 2023, and footway condition from 20.1% RED to around 10%. That past investment explains
                why A-road RED fell to 4% and U-road RED dropped from 13% to 10–11%.
              </p>
              <p className="text-surface-600 text-sm">
                But the council is explicit: the borrowing programme is over, and future condition depends on
                external funding that may not match what was spent before.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Climate and Weather Admissions</h3>
              <p className="text-amber-800 text-sm mb-3">
                Stockport also acknowledges that "Climate change and annual weather fluctuations will also
                continue to have an impact on the conditions of the network" — including freeze-thaw action,
                extreme heat causing "fatting up" of surface dressing, and intense rainfall causing flooding
                across the borough.
              </p>
              <p className="text-amber-800 text-sm">
                Documented awareness of accelerated deterioration from weather raises the bar for what a
                "reasonable" inspection regime looks like after severe winters.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending detail */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Where The Money Goes — And Where It Does Not
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of capital and revenue spend from Stockport's transparency report
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-emerald-700">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-rose-700">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2025/26 proj.</td>
                  <td className="px-4 py-3 text-center">£6.03m</td>
                  <td className="px-4 py-3 text-center">tbc</td>
                  <td className="px-4 py-3 text-center">£5.0m</td>
                  <td className="px-4 py-3 text-center font-bold text-amber-600">23%</td>
                  <td className="px-4 py-3 text-center">tbc</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024/25</td>
                  <td className="px-4 py-3 text-center">£5.27m</td>
                  <td className="px-4 py-3 text-center text-rose-600">£4.1m</td>
                  <td className="px-4 py-3 text-center">£4.92m</td>
                  <td className="px-4 py-3 text-center">45%</td>
                  <td className="px-4 py-3 text-center">5.12%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023/24</td>
                  <td className="px-4 py-3 text-center">£5.98m</td>
                  <td className="px-4 py-3 text-center">£5.98m</td>
                  <td className="px-4 py-3 text-center">£4.28m</td>
                  <td className="px-4 py-3 text-center">35%</td>
                  <td className="px-4 py-3 text-center">3.29%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022/23</td>
                  <td className="px-4 py-3 text-center">£4.77m</td>
                  <td className="px-4 py-3 text-center text-emerald-600">£9.69m</td>
                  <td className="px-4 py-3 text-center">£3.6m</td>
                  <td className="px-4 py-3 text-center">27%</td>
                  <td className="px-4 py-3 text-center">0.18%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021/22</td>
                  <td className="px-4 py-3 text-center">£5.36m</td>
                  <td className="px-4 py-3 text-center text-emerald-600">£13.51m</td>
                  <td className="px-4 py-3 text-center">£3.92m</td>
                  <td className="px-4 py-3 text-center">41%</td>
                  <td className="px-4 py-3 text-center">0.02%</td>
                </tr>
                <tr>
                  <td className="px-4 py-3 font-mono">2020/21</td>
                  <td className="px-4 py-3 text-center">£5.47m</td>
                  <td className="px-4 py-3 text-center text-emerald-600">£11.88m</td>
                  <td className="px-4 py-3 text-center">£3.87m</td>
                  <td className="px-4 py-3 text-center">28%</td>
                  <td className="px-4 py-3 text-center">0.3%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Planned Maintenance — Just 5km</h3>
              <p className="text-surface-600 text-sm mb-3">
                Stockport plans to treat 5.02km of carriageway surface and 1.02km of footway in 2025/26 —
                on a 969.8km road network. That is roughly 0.5% of carriageways receiving planned surface treatment
                this year.
              </p>
              <p className="text-surface-600 text-sm">
                Structure maintenance is planned for seven named bridges and retaining walls including Norbury
                Hollow Bridge, Chorlton Bridge, and Merseyway drainage in Stockport Central.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Why GREEN Spend Still Helps The Council</h3>
              <p className="text-amber-800 text-sm">
                Historic capital spend far exceeded DfT allocations during the HIP years (£13.51m capital against
                £5.36m allocated in 2021/22). That investment history supports Stockport's GREEN spend scorecard.
                But the latest year tells a different story: £4.1m capital spend against £5.27m allocated, with
                preventative share projected to halve.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Post-HIP AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Stockport invested heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — historic capital investment well above DfT allocations
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> £100.6m HIP cut overall RED roads from ~19% to ~10%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> A-road RED halved from 6% to 4% over five years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Documented asset management policies aligned to Well-managed highway infrastructure
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GAIST camera surveys and LCRIG innovation membership
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a structured Section 58 defence on A-roads and routes with recent HIP treatment. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — 11% of 798km of U-roads in RED; B/C green fell to 40%
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Two-year condition survey cycle across the borough
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> HIP finished — council admits future condition depends on external funding
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Preventative capital projected at 23% for 2025/26; only 5.02km of planned carriageway treatment
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 16,482 carriageway defects in five years — defects form faster than planned maintenance covers
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council acknowledges climate and weather impacts on network deterioration
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend and a completed £100.6m investment programme, your claim lives or dies on
              the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on a U-road, the two-year survey cycle and 11% RED rate are your strongest structural arguments</li>
              <li>• Whether the defect appeared after the HIP programme ended and funding shifted to main-road treatments</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Stockport's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Stockport?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A post-HIP council demands a specific claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No U-road survey-cycle argument</li>
                <li>• No prior-report search</li>
                <li>• No HIP funding-gap analysis</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 798km U-road network and 11% RED documented</li>
                <li>✅ Two-year GAIST survey cycle argued</li>
                <li>✅ 16,482 defects in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Stockport</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=stockport"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Stockport Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Stockport has a GREEN spend scorecard — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but Stockport's overall rating is AMBER because of road
                condition. Section 58 turns on whether the specific defect that damaged your vehicle was reasonably
                inspected and repaired — not on aggregate spend. In 2024/25 capital spend was £4.1m against a DfT
                allocation of £5.27m, and preventative maintenance is projected at just 23% for 2025/26.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 797.7km — 82% of Stockport's 969.8km carriageway network. At the latest survey,
                11% of U-roads were in RED condition — roughly 88km of residential streets and estate roads. The
                council assesses carriageway and footway condition over a two-year period, so any given road may go
                up to two years between condition surveys.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the finished Highway Investment Programme affect my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                It can strengthen it. Stockport borrowed £100.6m for its Highway Investment Programme to cut RED
                roads to 10%, but the council states this extra spending ceased as of 2023/24 and "the condition
                of the network going forwards will be more dependent on the level of external funding." That is
                an admission that past improvements may not be sustained.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Why does Stockport only publish RED percentages for U-roads?
              </h3>
              <p className="text-surface-600 text-sm">
                The DfT template requires only RED-category percentages for unclassified roads. Stockport does not
                publish amber or green breakdowns for U-roads in its transparency report — so you cannot see what
                proportion is borderline versus genuinely good. For claims, the 11% RED figure and the two-year
                survey cycle are the structural data points.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                The pothole figures include drainage repairs — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                No — read the footnote, do not dismiss the scale. Stockport's figures count carriageway defects
                attended to under its highway safety inspection and repair policy, including drainage investigations.
                The council filled 3,716 such defects in 2024/25 alone and anticipates approximately 4,200 in 2025/26.
                That volume shows how many road-surface failures the network produces each year.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Stockport surveys condition over two years — what does that mean for Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                The council states that "General highway condition of carriageways and footways within in the borough
                are assessed over a 2 year period." For Section 58, ask when your road was last GAIST-surveyed,
                whether routine safety inspections under the council's Inspection and Repair Policy would have caught
                the defect between surveys, and whether prior reports gave the council actual notice regardless of
                survey timing.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.stockport.gov.uk/local-highways-maintenance-transparency-report-2025"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Stockport Council Local Highways Maintenance Transparency Report 2025
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
